import { useState, useEffect, useContext, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Appointment from "./pages/Appointment";
import Article from "./pages/Article";
import AppointmentDetails from "./pages/AppointmentDetails";
import ArticleDetails from "./pages/ArticleDetails";
import Login from "./pages/Login";
import { supabase } from "./supabaseClient";
import { ThemeContext } from "./ThemeContext";
import logo from "./assets/logo.png";

export default function AppWrapper() {
  // Wrap App in Router
  return (
    <Router>
      <App />
    </Router>
  );
}

function App() {
  const theme = useContext(ThemeContext);
  const [user, setUser] = useState(null);
  const [activeMenu, setActiveMenu] = useState(localStorage.getItem("activeMenu") || "dashboard");
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const sidebarRef = useRef();
  const navigate = useNavigate();

  const SIDEBAR_WIDTH = 200;
  const SIDEBAR_COLLAPSED = 60;

  const [loading, setLoading] = useState(true);

  // Handle window resize for auto-collapse
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setSidebarExpanded(!mobile);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Supabase session
  useEffect(() => {
    document.body.style.backgroundColor = theme.background;
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setUser(data.session.user);
      }
      setLoading(false); // done checking
    });
  }, [theme.darkMode]);

  // Collapse sidebar on click outside (mobile only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobile &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        setSidebarExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile]);

  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
    localStorage.setItem("activeMenu", menu);
    if (isMobile) setSidebarExpanded(false);
    navigate(menu === "dashboard" ? "/" : `/${menu}`);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    localStorage.removeItem("activeMenu");
  };

  if (loading) return null; // or a spinner
  if (!user) return <Login onLogin={setUser} />;


  const currentSidebarWidth = sidebarExpanded ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED;

  const sidebarStyle = {
    width: currentSidebarWidth,
    minWidth: currentSidebarWidth,
    maxWidth: currentSidebarWidth,
    height: "100vh",
    backgroundColor: theme.cardBackground,
    padding: sidebarExpanded ? 20 : 8,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "width 0.3s ease, padding 0.3s ease",
    borderRight: `1px solid ${theme.darkMode ? "#555" : "#ccc"}`,
    position: "fixed",
    left: 0,
    top: 0,
    zIndex: 1000,
    boxShadow: sidebarExpanded && isMobile ? "2px 0 12px rgba(0,0,0,0.2)" : "none",
  };

  const mainContentStyle = {
    flex: 1,
    marginLeft: currentSidebarWidth,
    padding: 20,
    background: theme.background,
    minHeight: "100vh",
    transition: "margin-left 0.3s ease",
    overflowX: "auto",
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      <nav ref={sidebarRef} style={sidebarStyle}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {!sidebarExpanded && (
            <button
              onClick={() => setSidebarExpanded(true)}
              style={{
                marginBottom: 10,
                background: "none",
                border: "none",
                fontSize: 24,
                color: theme.text,
                cursor: "pointer",
              }}
              title="Expand sidebar"
            >
              ☰
            </button>
          )}

          <img
            src={logo}
            alt="Logo"
            style={{
              width: sidebarExpanded ? 100 : 40,
              height: "auto",
              borderRadius: 10,
              marginBottom: sidebarExpanded ? 20 : 0,
              transition: "width 0.3s ease, margin 0.3s ease",
            }}
          />

          {sidebarExpanded && (
            <ul style={{ listStyle: "none", padding: 0, width: "100%" }}>
              {[
                { key: "dashboard", label: "Dashboard" },
                { key: "appointments", label: "Appointments" },
                { key: "articles", label: "Articles" },
              ].map((item) => (
                <li
                  key={item.key}
                  onClick={() => handleMenuClick(item.key)}
                  style={{
                    padding: 10,
                    marginBottom: 5,
                    borderRadius: 5,
                    cursor: "pointer",
                    backgroundColor: activeMenu === item.key ? theme.primary : "transparent",
                    color: activeMenu === item.key ? "white" : theme.text,
                  }}
                >
                  {item.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        {sidebarExpanded && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              onClick={theme.toggleTheme}
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 5,
                border: "none",
                backgroundColor: theme.primary,
                color: theme.buttonText,
                cursor: "pointer",
              }}
            >
              {theme.darkMode ? "Light Mode" : "Dark Mode"}
            </button>
            <button
              onClick={handleLogout}
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 5,
                border: "none",
                backgroundColor: "#dc2626",
                color: "white",
                cursor: "pointer",
              }}
            >
              Logout
            </button>
          </div>
        )}
      </nav>

      {/* Main content */}
      <div style={mainContentStyle}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/appointments" element={<Appointment />} />
          <Route path="/articles" element={<Article />} />
          <Route path="/appointments/:id" element={<AppointmentDetails />} />
          <Route path="/articles/:documentId" element={<ArticleDetails />} />
        </Routes>
      </div>
    </div>
  );
}
